import { DataStorageEnums, GetDataBody, Key } from 'dashboard-services/api/dataStorage';
import { ActionService, CommonService, ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';

import ReferenceDataService from 'services/ReferenceDataService';

import { basicCustomInsightActions } from '../';
import basicCustomInsightConsts from './basicConsts';

import dayjs from 'dayjs';

export default (() => {
  const AXIS_POSITION = basicCustomInsightConsts.AXIS_POSITION
  // Defined in basicConsts so services can read them without importing the actions; still exposed here for existing callers.
  const { BASIC_CHART_TYPES, CHART_SOURCES, CHART_TYPES } = basicCustomInsightConsts
  const ADD_AXIS_DATA = 'ADD_AXIS_DATA_CUSTOM_INSIGHT'
  const addAxisDataReducer = ActionService.makeActionCreator(ADD_AXIS_DATA, 'index', 'right', 'data')
  const addAxisData = (index, right = false, data = { dynamic: false }) => dispatch => dispatch(addAxisDataReducer(index, right, data))

  const ADD_CONDITIONAL_FORMATTING = 'ADD_CONDITIONAL_FORMATTING_CHART_CUSTOM_INSIGHT'
  const addConditionalFormatting = ActionService.makeActionCreator(ADD_CONDITIONAL_FORMATTING, 'index', 'data')

  const REMOVE_CONDITIONAL_FORMATTING = 'REMOVE_CONDITIONAL_FORMATTING_CHART_CUSTOM_INSIGHT'
  const removeConditionalFormatting = ActionService.makeActionCreator(REMOVE_CONDITIONAL_FORMATTING, 'conditionalFormattingIndex', 'tileIndex')

  const CHANGE_CONDITIONAL_FORMATTING = 'CHANGE_CONDITIONAL_FORMATTING_CHART_CUSTOM_INSIGHT'
  const changeConditionalFormatting = ActionService.makeActionCreator(CHANGE_CONDITIONAL_FORMATTING, 'value', 'name', 'conditionalFormattingIndex', 'tileIndex')
  
  const CHANGE_TOOLTIP_CONFIG = 'CHANGE_TOOLTIP_CONFIG_CHART_CUSTOM_INSIGHT'
  const changeTooltipConfig = ActionService.makeActionCreator(CHANGE_TOOLTIP_CONFIG, 'value', 'name', 'tooltipIndex', 'tileIndex')

  const CHANGE_TOOLTIP_DATA = 'CHANGE_TOOLTIP_DATA_CHART_CUSTOM_INSIGHT'
  const changeTooltipData = ActionService.makeActionCreator(CHANGE_TOOLTIP_DATA, 'value', 'tooltipId')

  const CHANGE_AXIS_DATA = 'CHANGE_AXIS_DATA_CUSTOM_INSIGHT'
  const changeAxisData = ActionService.makeActionCreator(CHANGE_AXIS_DATA, 'index', 'right', 'value', 'name')

  const CHANGE_ELEMENT_IN_AXIS_DATA = 'CHANGE_ELEMENT_IN_AXIS_DATA_CUSTOM_INSIGHT'
  const changeElementInAxisData = ActionService.makeActionCreator(CHANGE_ELEMENT_IN_AXIS_DATA, 'value', 'name', 'dataIndex', 'tileIndex', 'right')

  const REMOVE_AXIS_DATA = 'REMOVE_AXIS_DATA_CUSTOM_INSIGHT'
  const removeAxisData = ActionService.makeActionCreator(REMOVE_AXIS_DATA, 'dataIndex', 'tileIndex', 'right')

  const REORDER_AXIS_DATA = 'REORDER_AXIS_DATA_CUSTOM_INSIGHT'
  const reorderAxisData = ActionService.makeActionCreator(REORDER_AXIS_DATA, 'from', 'to', 'tileIndex', 'right')

  const DUPLICATE_AXIS_DATA = 'DUPLICATE_AXIS_DATA_CUSTOM_INSIGHT'
  const duplicateAxisData = ActionService.makeActionCreator(DUPLICATE_AXIS_DATA, 'dataIndex', 'tileIndex', 'right')

  const SET_DATA_REF = 'SET_DATA_REF_CUSTOM_INSIGHT'
  const setDataRef = ActionService.makeActionCreator(SET_DATA_REF, 'value', 'index', 'dataRef')

  const operations = {
    SUM: {
      label: "Sum",
      value: "SUM"
    },
    AVERAGE: {
      label: "Average",
      value: "Average"
    }
  }

  const APPLY_TO_AXIS = {
    LEFT: {
      label: "Left",
      value: AXIS_POSITION.LEFT
    },
    RIGHT: {
      label: "Right",
      value: AXIS_POSITION.RIGHT
    },
    BOTH: {
      label: "Both",
      value: "both"
    }
  }

  const getEmptyChartData = ({ label, color }) => ({ 
    label, 

    dynamic: false,
    columnXAxisIsNumber: false,
    column: undefined,
    groupName: undefined,
    columnXAxis: undefined,
    columnXAxisTimezone: undefined,
    symbolList: [],
    movingAverage: false,
    movingAverageValue: undefined,

    operation: undefined,

    chartType: CHART_TYPES.LINE.value, 
    lineThickness: undefined,
    lineStyle: undefined,
    lineOpacity: undefined,
    withReferenceLine: false,
    isHistogram: false,
    dotSize: "5",
    color,
  })

  const getSeasonFirstMonth = ({ seasonFirstMonth, seasonMonth, data = [], dateFormat = TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE }) => (_, getState) => {
    const definedTime = getState().myAccountState.settingsState.definedTime
    let localMonth = seasonFirstMonth;
    if(seasonFirstMonth === basicCustomInsightConsts.CURRENT_MONTH) {
      localMonth = TimeService.getUserFormattedDate(undefined, definedTime, "M")
    } else if(seasonFirstMonth === basicCustomInsightConsts.FIRST_RECORDED_DATE) {
      let smallestMonth = data.reduce((prev, time) => {
        const thisMonth = Number(TimeService.getUtcMonth(time, TimeService.translateFormat(dateFormat)))
        return thisMonth < prev ? thisMonth : prev;
      }, 13)
      if(smallestMonth === 13) {
        smallestMonth = 1;
      }
      localMonth = smallestMonth;
    } else if(seasonFirstMonth === basicCustomInsightConsts.LAST_RECORDED_DATE) {
      let biggestMonth = data.reduce((prev, time) => {
        const thisMonth = Number(TimeService.getUtcMonth(time, TimeService.translateFormat(dateFormat)))
        return thisMonth > prev ? thisMonth : prev;
      }, 0)
      if(biggestMonth === 0) {
        biggestMonth = 12;
      }
      localMonth = biggestMonth;
    }
    if(seasonMonth === basicCustomInsightConsts.SEASON_MONTH.FIRST.value) {
      return localMonth;
    } else {
      return Number(localMonth) + 1
    }
  }

  const APPLY_TO_STANDARD = {
    ALL_DATA: {
      label: "All data",
      value: "ALL_DATA"
    }
  }

  const APPLY_TO_AREA_DIFF = {
    LEFT: {
      label: "Left Area Diff",
      value: "LEFT_AREA_DIFF"
    },
    RIGHT: {
      label: "Right Area Diff",
      value: "RIGHT_AREA_DIFF"
    }
  }

  const getDefaultAreaDiffFormatting = ({ right = false } = {}) => {
    const defaultFormatting = {}
    Object.entries(basicCustomInsightConsts.EMPTY_CONDITION_FORMATTING_AREA_DIFF_ITEM).forEach(([name, { defaultValue }]) => {
      defaultFormatting[name] = defaultValue
    })
    return {
      ...defaultFormatting,
      applyTo: right ? APPLY_TO_AREA_DIFF.RIGHT.value : APPLY_TO_AREA_DIFF.LEFT.value,
      label: right ? APPLY_TO_AREA_DIFF.RIGHT.label : APPLY_TO_AREA_DIFF.LEFT.label
    }
  }

  const normalizeAreaDiffFormatting = ({ conditionalFormatting = [], withAreaDiff = false } = {}) => {
    const areaDiffValues = Object.values(APPLY_TO_AREA_DIFF).map(({ value }) => value),
          normalizedFormatting = (conditionalFormatting || []).filter(item => withAreaDiff || !areaDiffValues.includes(item?.applyTo))
    if(!withAreaDiff) {
      return normalizedFormatting
    }
    const formatting = [...normalizedFormatting]
    if(!formatting.some(item => item?.applyTo === APPLY_TO_AREA_DIFF.LEFT.value)) {
      formatting.push(getDefaultAreaDiffFormatting())
    }
    if(!formatting.some(item => item?.applyTo === APPLY_TO_AREA_DIFF.RIGHT.value)) {
      formatting.push(getDefaultAreaDiffFormatting({ right: true }))
    }
    return formatting
  }

  const APPLY_TO_CURVES = {
    ALL_DATA: {
      label: "All data",
      value: "ALL_DATA"
    },
    NEWEST_CURVES: {
      label: "Newest Curves",
      value: "NEWEST_CURVES"
    },
    CURVES_WITH_INDEX: {
      label: "Curves with index",
      value: "CURVES_WITH_INDEX"
    }
  }

  const CONDITION = {
    IS_EQUAL: "==",
    IS_NOT_EQUAL: "!=",
    IS_ONE_OF: "!includes",
    IS_NOT_ONE_OF: "includes",
    MATCH_REGEXP: "regex",
    CONTAINS: "contains",
    NOT_CONTAINS: "!contains"
  }

  const CONDITIONS = [{
    label: "Is equal to",
    value: CONDITION.IS_EQUAL
  }, {
    label: "Is not equal to",
    value: CONDITION.IS_NOT_EQUAL
  }, {
    label: "Contains",
    value: CONDITION.CONTAINS
  }, {
    label: "Does not contain",
    value: CONDITION.NOT_CONTAINS
  }, {
    label: "Is one of",
    value: CONDITION.IS_ONE_OF
  }, {
    label: "Is not one of",
    value: CONDITION.IS_NOT_ONE_OF
  }, {
    label: "Match RegExp",
    value: CONDITION.MATCH_REGEXP
  }]

  const getUniqueKeysFromSymbols = symbols => {
    const uniqueKeys = new Set()
    Object.values(symbols || {}).forEach(symbol => Object.keys(symbol.symbols || {}).forEach(key => uniqueKeys.add(key)))
    return Array.from(uniqueKeys).map(uk => ({ label: uk, value: uk }))
  }

  const getUniqueValuesFromSymbols = ({ symbols, symbolKey }) => {
    const uniqueValues = new Set()
    Object.values(symbols || {}).forEach(symbol => Object.entries(symbol.symbols || {}).filter(([k]) => k === symbolKey).forEach(([, value]) => uniqueValues.add(value)))
    return Array.from(uniqueValues).sort((a, b) => String(a).localeCompare(b)).map(uk => ({ label: uk, value: uk }))
  }

  const getUniqueColumnsFromSymbols = symbols => {
    const unique = new Set()
    Object.values(symbols || {}).forEach(symbol => unique.add(symbol.valueName))
    return Array.from(unique).map(uk => ({ label: uk, value: uk }))
  }

  const getSourceTileBody = ({ columns, symbols, metadatas, groupName, timezone, decimalPrecision, to, from, specificDate, filters, dataRange, lastValue, lastType, dateFormat, withMetadata = false, patternExactMatch = false, symbolValuesExactMatch = true, metaDataExactMatch = true  } = {}, csv, stats) => {
    let builder = new GetDataBody.Builder()
  
    if((to || from) && dataRange === DataStorageEnums.DATA_RANGE.BETWEEN) {
      builder = builder
        .withRange(DataStorageEnums.DATA_RANGE.BETWEEN)
        .withStartDate(from ? dayjs(from +  "T00:00:00.000").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE): undefined)
        .withEndDate(to ? dayjs(to +  "T23:59:59.999").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined)
    } else if(dataRange === DataStorageEnums.DATA_RANGE.LAST) {
      builder = builder
        .withRange(DataStorageEnums.DATA_RANGE.LAST)
        .withLastType(lastType || DataStorageEnums.LAST_TYPE.VALUE)
        .withLastTypeAmount(lastValue || basicCustomInsightConsts.ABSOLUTE_SIZE)
    } else if(dataRange === basicCustomInsightConsts.CUSTOM_DATE_RANGES.SPECIFIC_DATE.value) {
      const specificDateFrom = specificDate ? dayjs.utc(specificDate + "T00:00:00.000").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined,
            specificDateTo = specificDate ? dayjs.utc(specificDate + "T23:59:59.999").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined
      builder = builder
              .withStartDate(specificDateFrom === "undefined" || specificDateFrom === "Invalid Date" ? undefined : specificDateFrom)
              .withEndDate(specificDateTo === "undefined" || specificDateTo === "Invalid Date" ? undefined : specificDateTo)
              .withRange(DataStorageEnums.DATA_RANGE.BETWEEN)
    } else {
      builder = builder
        .withRange(DataStorageEnums.DATA_RANGE.ALL)
        
    }

    const allKeysCombinations = basicCustomInsightActions.generateKeysCombinations({ symbols, metadatas })
    allKeysCombinations?.forEach(({ symbols, metadatas }) => {
      const key = new Key.Builder()
        .withColumns(columns?.indexOf("*") > -1 ? undefined : columns)
        .withSymbols(symbols)
        .withMetadatas(metadatas)
        .withGroupName(groupName)
        .withPattern(true)
        .withSymbolValuesExactMatch(symbolValuesExactMatch)
        .withPatternExactMatch(patternExactMatch)
        .withMetadataExactMatch(metaDataExactMatch)

        builder = builder.withKey(key.build().serialize())
    })
  
    const body = builder
      .withDateFormat(dateFormat)
      .withTimeZone(timezone)
      .withFormatType(csv ? DataStorageEnums.DATA_FORMAT.NCSV : DataStorageEnums.DATA_FORMAT.UI)
      .withSkipEmptyColumns(true)
      .withMetadata(withMetadata)
      .withDecimalPrecision(decimalPrecision === "" ? undefined : decimalPrecision)
      .withFilters(filters)
      .withStats(stats)
      .build()
      .serialize()
    return body
  }

  const getSourceTileData = ({ 
    getDataCall,
    onError, 
    onComplete = () => {}, 
    onHandleRequest = () => {}, 
    inputParams, 
    body: bodyOuter = undefined,
    csv = false,
    stats = false
  }) => {
    const body = bodyOuter || getSourceTileBody(inputParams, csv, stats)
    return getTileData({ 
      body, 
      getDataCall,
      onError,
      onComplete,
      onHandleRequest,
      csv,
      // Data of input params comes from their provider (Target). Without it, an insight opened again asked the local environment.
      // /ts has no /envs version, so for All providers the local environment is asked as before
      provider: basicCustomInsightConsts.getProviderTarget(inputParams?.provider)
    })
  }

  const getTileData = ({ 
    body,
    getDataCall,
    onComplete = () => {}, 
    onHandleRequest = () => {}, 
    onError,
    csv = false,
    provider
  }) => {
    onHandleRequest()
    return getDataCall({ body, csv, absoluteSize: basicCustomInsightConsts.ABSOLUTE_SIZE, omitNulls: !csv, provider })
      .catch(e => {
        console.error(e)
        return onError?.()
      })
      .then(response => csv ? response.text() : Promise.resolve(response))
      .then(response => {
        if(response) {
          // Series from a specific provider keep it as their environment, it decides which groups get insight roles
          const symbols = provider && response.symbols
            ? Object.fromEntries(Object.entries(response.symbols).map(([id, symbol]) => [id, ReduxService.updateObject(symbol, { sources: [provider] })]))
            : response.symbols
          onComplete(!csv ? { symbols, values: response.values, metadata: response.metadataMap, stats: response.statistics } : response)
        }
        return response;
      })
      .finally(response => {
        onHandleRequest({ isResponse: true })
        return response;
      })
  }

  const LEGEND_SIZES = {
    STANDARD: {
      label: "Standard",
      value: undefined
    },
    BIG: {
      label: "Big",
      value: "--is-big"
    }
  }

  const TITLE_SIZES = {
    SMALL: {
      label: "Small",
      value: "--is-small"
    },
    STANDARD: {
      label: "Standard",
      value: undefined
    },
    BIG: {
      label: "Big",
      value: "--is-big"
    },
    HUGE: {
      label: "Huge",
      value: "--is-huge"
    }
  }

  const LEGEND_SORTING = {
    DESC: {
      label: "Descending",
      value: "DESC"
    },
    ASC: {
      label: "Ascending",
      value: "ASC"
    }
  }

  const areTooltipDataEqual = (first, second) => {
    if(first === second) {
      return true
    }
    if(Array.isArray(first) || Array.isArray(second)) {
      return Array.isArray(first) && Array.isArray(second) && first.length === second.length && first.every((item, index) => areTooltipDataEqual(item, second[index]))
    }
    if(first && second && typeof first === "object" && typeof second === "object") {
      const firstKeys = Object.keys(first),
            secondKeys = Object.keys(second)
      return firstKeys.length === secondKeys.length && firstKeys.every(key => Object.prototype.hasOwnProperty.call(second, key) && areTooltipDataEqual(first[key], second[key]))
    }
    return false
  }

  const setTooltipData = ({ mergedTooltipData, id }) => (dispatch, getState) => {
    const tooltipData = getState().insightState.customState?.tooltipData
    if(areTooltipDataEqual(tooltipData?.[id], mergedTooltipData)) {
      return
    }
    dispatch(basicCustomInsightActions.onChange(ReduxService.updateObject(tooltipData, { [id]: mergedTooltipData }), 'tooltipData'))
  }

  const getMergedTooltipArrays = ({ tooltipData, index }) => (dispatch, getState) => {
    if(!tooltipData) {
      return 
    }
    const tooltipConfig = getState().insightState.customState.content?.tiles?.[index]?.tooltipConfig
    if(!tooltipConfig || tooltipConfig?.length === 0 ) {
      return tooltipData
    }
    const configMap = Object.fromEntries(tooltipConfig.map(item => [item.value, item])),
          mergedArray = tooltipData.map(item => {
            const config = configMap[item.value]
            if(!config) {
              return item
            } else {
              return ReduxService.updateObject(item, {
                label: config.label ?? config.value,
                color: item.isError ? config.color ?? item.color : item.color,
                showInTooltip: item.isError && config.showInTooltip !== undefined ? config.showInTooltip : (item.isError === undefined ? undefined : !item.isError)
              })
            }
          }),
          sortedArray = mergedArray.sort((a, b) => {
            const indexA = tooltipConfig.findIndex(config => config.value === a.value)
            const indexB = tooltipConfig.findIndex(config => config.value === b.value)
            if (indexA === -1) return 1
            if (indexB === -1) return -1
            return indexA - indexB
          })
    
    return sortedArray
  }

  const getTileFromState = ({ getState, tileIndex }) => getState().insightState.customState.content?.tiles?.[tileIndex] || {}

  const getItemFormattingKeys = item => {
    if(ReferenceDataService.isBandItem(item)) {
      return Object.values(ReferenceDataService.getBandKeys({ item }))
    }
    return ReferenceDataService.isLineItem(item) ? [ReferenceDataService.getFormattingKey({ item })] : []
  }

  const removeFormattingByKeys = ({ keys, tileIndex }) => (dispatch, getState) => (getTileFromState({ getState, tileIndex }).conditionalFormatting || [])
    .map((entry, index) => keys.includes(entry?.applyTo) ? index : undefined)
    .filter(index => index !== undefined)
    .reverse()
    .forEach(index => dispatch(removeConditionalFormatting(index, tileIndex)))

  const addMissingFormatting = ({ item, tileIndex }) => (dispatch, getState) => {
    const conditionalFormatting = getTileFromState({ getState, tileIndex }).conditionalFormatting || []
    ReferenceDataService.getDefaultFormatting({ item })
      .filter(formatting => !conditionalFormatting.some(entry => entry?.applyTo === formatting.applyTo))
      .forEach(formatting => dispatch(addConditionalFormatting(tileIndex, formatting)))
  }

  const getLineFormattingIndex = ({ getState, item, tileIndex }) => {
    const key = ReferenceDataService.getFormattingKey({ item })
    return (getTileFromState({ getState, tileIndex }).conditionalFormatting || []).findIndex(entry => entry?.applyTo === key)
  }

  const relabelLineFormatting = ({ item, tileIndex }) => (dispatch, getState) => {
    const index = getLineFormattingIndex({ getState, item, tileIndex })
    if(index > -1) {
      dispatch(changeConditionalFormatting(ReferenceDataService.getItemLabel({ item }), "label", index, tileIndex))
    }
  }

  // Moving to or from a date labelled value (Today, Expiry date) switches between the date label look and the regular line look.
  const syncDateLineFormatting = ({ item, previousItem, tileIndex }) => (dispatch, getState) => {
    const index = getLineFormattingIndex({ getState, item, tileIndex })
    if(index > -1 && ReferenceDataService.isDateLabelLine(item) !== ReferenceDataService.isDateLabelLine(previousItem)) {
      Object.entries(ReferenceDataService.getLineLabelFormatting({ item }))
        .forEach(([field, value]) => dispatch(changeConditionalFormatting(value, field, index, tileIndex)))
    }
  }

  const REFERENCE_VALUE_FIELDS = ["date", "xValue", "yValue", "yValueType"]

  // Today and Expiry date lines keep no value label: they are drawn with their date until the user renames them.
  const getStoredValueLabel = ({ item }) => ReferenceDataService.isDateLabelLine(item) ? undefined : ReferenceDataService.getValueLabel({ item })

  // A renamed item keeps its label until a value is chosen again; an empty name restores the value label.
  const getLabelledReferenceItem = ({ changes, item }) => {
    const label = changes.label !== undefined
      ? changes.label
      : REFERENCE_VALUE_FIELDS.some(field => field in changes) ? getStoredValueLabel({ item }) : item.label
    return label
      ? { ...item, label }
      : Object.fromEntries(Object.entries(item).filter(([name]) => name !== "label"))
  }

  // A new type or axis starts the item over; a non-specific X value or a cleared date drops the picked date.
  const getChangedReferenceItem = ({ changes, item }) => {
    if(changes.type !== undefined && changes.type !== item.type) {
      return { id: item.id, type: changes.type }
    }
    if(changes.axis !== undefined && changes.axis !== item.axis) {
      return { axis: changes.axis, id: item.id, type: item.type }
    }
    const { date, ...changedItem } = { ...item, ...changes }
    return getLabelledReferenceItem({
      changes,
      item: changedItem.xValue === basicCustomInsightConsts.REFERENCE_X_VALUES.SPECIFIC_DATE.value && date ? { ...changedItem, date } : changedItem
    })
  }

  const syncReferenceItemFormatting = ({ item, previousItem, tileIndex }) => dispatch => {
    const isRestarted = previousItem.type !== item.type || previousItem.axis !== item.axis
    if(isRestarted) {
      dispatch(removeFormattingByKeys({ keys: getItemFormattingKeys(previousItem), tileIndex }))
    }
    if(ReferenceDataService.isBandItem(item)) {
      dispatch(addMissingFormatting({ item, tileIndex }))
      return
    }
    if(ReferenceDataService.getDefaultFormatting({ item }).length === 0) {
      dispatch(removeFormattingByKeys({ keys: getItemFormattingKeys(item), tileIndex }))
      return
    }
    dispatch(relabelLineFormatting({ item, tileIndex }))
    dispatch(syncDateLineFormatting({ item, previousItem, tileIndex }))
    dispatch(addMissingFormatting({ item, tileIndex }))
  }

  const setReferenceData = ({ referenceData, tileIndex }) => dispatch => dispatch(basicCustomInsightActions.onChangeTile(referenceData, "referenceData", tileIndex))

  const addReferenceData = ({ tileIndex }) => (dispatch, getState) => dispatch(setReferenceData({ referenceData: (getTileFromState({ getState, tileIndex }).referenceData || []).concat({ id: CommonService.generateUID() }), tileIndex }))

  const changeReferenceData = ({ changes, itemIndex, tileIndex }) => (dispatch, getState) => {
    const referenceData = getTileFromState({ getState, tileIndex }).referenceData || [],
          previousItem = referenceData[itemIndex]
    if(!previousItem) {
      return
    }
    const item = getChangedReferenceItem({ changes, item: previousItem })
    dispatch(setReferenceData({ referenceData: referenceData.map((current, index) => index === itemIndex ? item : current), tileIndex }))
    dispatch(syncReferenceItemFormatting({ item, previousItem, tileIndex }))
  }

  const removeReferenceData = ({ itemIndex, tileIndex }) => (dispatch, getState) => {
    const referenceData = getTileFromState({ getState, tileIndex }).referenceData || [],
          item = referenceData[itemIndex]
    if(!item) {
      return
    }
    dispatch(setReferenceData({ referenceData: referenceData.filter((_, index) => index !== itemIndex), tileIndex }))
    dispatch(removeFormattingByKeys({ keys: getItemFormattingKeys(item), tileIndex }))
    dispatch(basicCustomInsightActions.onChange(undefined, "expanded"))
  }

  const syncExpiryReferenceLine = ({ seasonalityType, tileIndex }) => (dispatch, getState) => {
    const referenceData = getTileFromState({ getState, tileIndex }).referenceData || []
    if(seasonalityType === basicCustomInsightConsts.SEASONALITY_TYPE.EXPIRY.value) {
      if(referenceData.some(ReferenceDataService.isExpiryLine)) {
        return
      }
      const item = ReferenceDataService.getExpiryLine({ id: CommonService.generateUID() })
      dispatch(setReferenceData({ referenceData: referenceData.concat(item), tileIndex }))
      dispatch(addMissingFormatting({ item, tileIndex }))
      return
    }
    const expiryLines = referenceData.filter(ReferenceDataService.isExpiryLine)
    if(expiryLines.length === 0) {
      return
    }
    dispatch(setReferenceData({ referenceData: referenceData.filter(item => !ReferenceDataService.isExpiryLine(item)), tileIndex }))
    dispatch(removeFormattingByKeys({ keys: expiryLines.flatMap(getItemFormattingKeys), tileIndex }))
  }

  return {
    addReferenceData,
    changeReferenceData,
    removeReferenceData,
    syncExpiryReferenceLine,
    ADD_AXIS_DATA,
    addAxisData,
    ADD_CONDITIONAL_FORMATTING,
    addConditionalFormatting,
    REMOVE_CONDITIONAL_FORMATTING,
    removeConditionalFormatting,
    CHANGE_CONDITIONAL_FORMATTING,
    changeConditionalFormatting,
    CHANGE_AXIS_DATA,
    changeAxisData,
    CHANGE_ELEMENT_IN_AXIS_DATA,
    changeElementInAxisData,
    REMOVE_AXIS_DATA,
    removeAxisData,
    DUPLICATE_AXIS_DATA,
    duplicateAxisData,
    SET_DATA_REF,
    setDataRef,
    operations,
    APPLY_TO_AXIS,
    getEmptyChartData,
    BASIC_CHART_TYPES,
    CHART_SOURCES,
    getSeasonFirstMonth,
    APPLY_TO_STANDARD,
    CONDITIONS,
    CONDITION,
    getUniqueKeysFromSymbols,
    getUniqueValuesFromSymbols,
    getUniqueColumnsFromSymbols,
    getSourceTileData,
    getSourceTileBody,
    reorderAxisData,
    REORDER_AXIS_DATA,
    LEGEND_SIZES,
    TITLE_SIZES,
    LEGEND_SORTING,
    CHANGE_TOOLTIP_CONFIG,
    changeTooltipConfig,
    CHANGE_TOOLTIP_DATA,
    changeTooltipData,
    getMergedTooltipArrays,
    setTooltipData,
    getTileData,
    CHART_TYPES,
    APPLY_TO_CURVES,
    APPLY_TO_AREA_DIFF,
    getDefaultAreaDiffFormatting,
    normalizeAreaDiffFormatting
  }
})()
